import React, { useRef, useState } from 'react';
import {
  createSelaDocument,
  startSelaDocumentProcessing,
  uploadSelaDocumentToS3,
  type SelaDocument,
  type SelaDocumentMetadata,
} from '../../services/selaApi';
import { recordV2ErrorStage, recordV2FunnelStep, recordV2Latency } from '../../utils/v2Rum';

const MAX_PDF_BYTES = 50 * 1024 * 1024;

export async function uploadSelaPdfDocument(
  projectId: string,
  file: File,
  metadata: SelaDocumentMetadata = {},
): Promise<SelaDocument> {
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    throw new Error('Only .pdf files are supported.');
  }
  if (file.size > MAX_PDF_BYTES) {
    throw new Error(`File exceeds the ${MAX_PDF_BYTES / (1024 * 1024)} MB limit.`);
  }
  const created = await createSelaDocument(projectId, file, metadata);
  await uploadSelaDocumentToS3(created.upload, file);
  return startSelaDocumentProcessing(projectId, created.document.documentId);
}

type SelaDocumentUploadButtonProps = {
  projectId: string;
  metadata?: SelaDocumentMetadata;
  label?: string;
  busyLabel?: string;
  className?: string;
  disabled?: boolean;
  onUploaded?: (document: SelaDocument) => void | Promise<void>;
  onError?: (message: string) => void;
};

export function SelaDocumentUploadButton({
  projectId,
  metadata = {},
  label = 'Upload document',
  busyLabel = 'Uploading...',
  className,
  disabled,
  onUploaded,
  onError,
}: SelaDocumentUploadButtonProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    const startedAt = performance.now();
    const workflow = metadata.workflowContext || 'library';
    setUploading(true);
    onError?.('');
    recordV2FunnelStep('document_upload', 'start', {
      workflow,
      source_page: workflow === 'timeline' ? 'roadmap' : 'documents',
    }, projectId);
    try {
      const document = await uploadSelaPdfDocument(projectId, file, metadata);
      await onUploaded?.(document);
      recordV2FunnelStep('document_upload', 'success', {
        workflow,
        document_id: document.documentId,
        source_page: workflow === 'timeline' ? 'roadmap' : 'documents',
      }, projectId);
      recordV2Latency('document_upload', performance.now() - startedAt, { workflow }, projectId);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : 'Upload failed.');
      recordV2FunnelStep('document_upload', 'failure', { workflow }, projectId);
      recordV2ErrorStage('document_upload', err, { workflow }, projectId);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        className={className || 'rounded-md bg-[#3251b7] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#28449f] disabled:opacity-60'}
      >
        {uploading ? busyLabel : label}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        disabled={disabled || uploading}
        onChange={(event) => void handleFile(event.target.files?.[0])}
      />
    </>
  );
}
